import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCredits } from '../../context/CreditContext';
import { STORAGE_KEYS } from '../../utils/constants';
import { languageAPI, nameSearchAPI, ebookAPI } from '../../utils/api';
import { trackLanguageChange } from '../../utils/analytics';
import { useScrollLock } from '../../hooks/useScrollLock';
import {
  FaUser,
  FaHeart,
  FaSignOutAlt,
  FaUserPlus,
  FaGlobe,
  FaChevronDown,
  FaBook,
  FaCalendarAlt,
  FaInfinity
} from 'react-icons/fa';
import './Header.css';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  // Kayıt Duvarı modeli: AI sorguları kredi düşmez — gösterilen sayı günlük
  // kotadaki kalan sorgu (3/gün), eski kredi bakiyesi DEĞİL (yeni kullanıcı
  // credits=0 ile "0 AI searches left" görüyordu).
  const { dailyQuota } = useCredits();
  const isUnlimited = !!(user?.unlimited_access_until && new Date(user.unlimited_access_until) > new Date());
  const navigate = useNavigate();
  const location = useLocation();

  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileAccountOpen, setIsMobileAccountOpen] = useState(false);
  const [isMobileLangOpen, setIsMobileLangOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchValue, setSearchValue] = useState('');
  const [ebookCount, setEbookCount] = useState(0);
  const [favCount, setFavCount] = useState(0);

  const searchInputRef = useRef(null);
  const profileToggleRef = useRef(null);
  const profileMenuRef = useRef(null);
  const langToggleRef = useRef(null);
  const langMenuRef = useRef(null);
  const mobileAccountToggleRef = useRef(null);
  const mobileLangToggleRef = useRef(null);

  const languages = [
    { code: 'de', name: 'Deutsch', nativeName: 'Deutsch' },
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'tr', name: 'Türkçe', nativeName: 'Türkçe' },
    { code: 'ar', name: 'العربية', nativeName: 'العربية' },
    { code: 'ms', name: 'Bahasa Melayu', nativeName: 'Bahasa Melayu' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  const userInitial = ((user?.name || user?.email || '?').trim()[0] || '?').toUpperCase();
  const userName = user?.name || user?.email?.split('@')[0] || '';

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileAccountOpen(false);
        setIsMobileLangOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Rota değişince tüm açılır menüleri kapat
  useEffect(() => {
    setIsLanguageDropdownOpen(false);
    setIsProfileDropdownOpen(false);
    setIsMobileAccountOpen(false);
    setIsMobileLangOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.header-lang-dropdown')) {
        setIsLanguageDropdownOpen(false);
      }
      if (!event.target.closest('.header-account')) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mobil sheet'ler için scroll kilidi
  useScrollLock(isMobileAccountOpen || isMobileLangOpen);

  // "/" kısayolu aramaya odaklanır
  useEffect(() => {
    const handleShortcuts = (e) => {
      const target = e.target;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleShortcuts);
    return () => document.removeEventListener('keydown', handleShortcuts);
  }, []);

  // Girişli kullanıcı için kitap + favori sayıları
  useEffect(() => {
    let cancelled = false;
    if (isAuthenticated && user?.id) {
      ebookAPI.getMyPurchases()
        .then((res) => {
          if (cancelled || !res?.success || !res.data) return;
          const data = res.data.data || res.data;
          setEbookCount((data?.purchases || []).length);
        })
        .catch(() => {});
      nameSearchAPI.getUserFavorites(user.id)
        .then((res) => {
          if (cancelled || !res?.success) return;
          const list = res.data?.data || res.data || [];
          setFavCount(Array.isArray(list) ? list.length : 0);
        })
        .catch(() => {});
    } else {
      setEbookCount(0);
      setFavCount(0);
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsProfileDropdownOpen(false);
    setIsMobileAccountOpen(false);
  };

  const handleLanguageChange = async (langCode) => {
    const prevLang = i18n.language;
    i18n.changeLanguage(langCode);
    localStorage.setItem('preferred-language', langCode);
    trackLanguageChange(prevLang, langCode);

    if (isAuthenticated) {
      try {
        await languageAPI.updateUserLanguage(langCode);
      } catch (error) {
        console.error('Failed to sync language to database:', error);
      }
    }

    setIsLanguageDropdownOpen(false);
    setIsMobileLangOpen(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    if (!q) {
      navigate('/name-meaning');
      return;
    }
    navigate(`/name-meaning?q=${encodeURIComponent(q)}`);
    setSearchValue('');
    searchInputRef.current?.blur();
  };

  // Dropdown klavye yardımcısı
  const handleDropdownKeyDown = (e, isOpen, toggleFn) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      toggleFn(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFn(!isOpen);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      toggleFn(true);
    }
  };

  const saveRedirect = () => {
    localStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_AUTH, window.location.pathname + window.location.search);
  };

  const planLine = isUnlimited
    ? t('header.premiumActive', 'Premium Aktif')
    : `${t('header.freePlan', 'Free plan')}${dailyQuota && Number.isFinite(dailyQuota.remaining) ? ` · ${t('header.searchesLeftToday', { count: dailyQuota.remaining })}` : ''}`;

  const renderLanguageChips = () => (
    <div className="hdp-lang">
      <span className="hdp-lang-label">{t('common.language', 'Language')}</span>
      <div className="hdp-lang-chips">
        {languages.map(lang => (
          <button
            key={lang.code}
            type="button"
            className={`hdp-lang-chip${lang.code === i18n.language ? ' active' : ''}`}
            onClick={() => handleLanguageChange(lang.code)}
          >
            {lang.nativeName}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
    <header className="header">
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main-content')?.focus();
          document.getElementById('main-content')?.scrollIntoView();
        }}
      >
        {t('a11y.skipToContent', 'Zum Inhalt springen')}
      </a>

      {/* ═══ Masaüstü ═══ */}
      {!isMobile && (
        <div className="desktop-header-layout">
          <Link to="/" className="header-logo" aria-label="BabynamenIman — Home">
            <img src="/BNI.png" alt="BabynamenIman" className="header-logo-image" fetchpriority="high" loading="eager" />
          </Link>

          <form className="header-search" role="search" onSubmit={handleSearchSubmit}>
            <svg className="header-search-glyph" aria-hidden="true" viewBox="0 0 24 24" focusable="false">
              <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
              <line x1="15.6" y1="15.6" x2="21" y2="21" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            <input
              ref={searchInputRef}
              type="search"
              className="header-search-input"
              placeholder={t('header.searchPlaceholder', "Look up a name's meaning…")}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              aria-label={t('header.searchPlaceholder', "Look up a name's meaning…")}
            />
            <kbd className="header-search-kbd" aria-hidden="true">/</kbd>
          </form>

          <div className="header-right">
            {isAuthenticated ? (
              <>
                <Link
                  to={isUnlimited ? '/profile' : '/credits'}
                  className={`header-unlimited-btn${isUnlimited ? ' is-active' : ''}`}
                  title={isUnlimited ? t('header.premiumActive', 'Premium Aktif') : t('header.getUnlimited', 'Sınırsız Al')}
                >
                  <FaInfinity className="header-unlimited-icon" aria-hidden="true" />
                  <span>{isUnlimited ? t('header.premiumActive', 'Premium Aktif') : t('header.getUnlimited', 'Sınırsız Al')}</span>
                </Link>

                <Link to="/my-ebooks" className="header-icon-btn" title={t('common.navigation.myEbooks', 'My E-Books')} aria-label={t('common.navigation.myEbooks', 'My E-Books')}>
                  <FaBook aria-hidden="true" />
                  {ebookCount > 0 && <span className="header-badge">{ebookCount}</span>}
                </Link>

                <div className="header-account">
                  <button
                    ref={profileToggleRef}
                    type="button"
                    className={`header-account-toggle${isProfileDropdownOpen ? ' open' : ''}`}
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    onKeyDown={(e) => handleDropdownKeyDown(e, isProfileDropdownOpen, setIsProfileDropdownOpen)}
                    aria-haspopup="true"
                    aria-expanded={isProfileDropdownOpen}
                    aria-label={t('a11y.profileMenu', 'Profile Menu')}
                  >
                    <span className="header-avatar" aria-hidden="true">{userInitial}</span>
                    <span className="header-account-name">{userName}</span>
                    <FaChevronDown className={`header-caret${isProfileDropdownOpen ? ' open' : ''}`} aria-hidden="true" />
                  </button>

                  {isProfileDropdownOpen && (
                    <div
                      ref={profileMenuRef}
                      className="header-account-menu"
                      role="menu"
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setIsProfileDropdownOpen(false); profileToggleRef.current?.focus(); } }}
                    >
                      <div className="header-account-head">
                        <span className="header-avatar header-avatar--lg" aria-hidden="true">{userInitial}</span>
                        <div className="header-account-head-text">
                          <span className="header-account-fullname">{user?.name || userName}</span>
                          <span className="header-account-plan">{planLine}</span>
                        </div>
                      </div>

                      <div className="header-account-items">
                        <Link to="/profile" className="header-account-item" role="menuitem" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaUser className="header-account-item-icon" aria-hidden="true" />
                          <span>{t('common.navigation.profile', 'Profile')}</span>
                        </Link>
                        <Link to="/favorites" className="header-account-item" role="menuitem" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaHeart className="header-account-item-icon" aria-hidden="true" />
                          <span>{t('common.navigation.favorites', 'Favorites')}</span>
                          {favCount > 0 && <span className="header-account-item-count">{favCount}</span>}
                        </Link>
                        <Link to="/my-ebooks" className="header-account-item" role="menuitem" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaBook className="header-account-item-icon" aria-hidden="true" />
                          <span>{t('common.navigation.myEbooks', 'My E-Books')}</span>
                          {ebookCount > 0 && <span className="header-account-item-count">{ebookCount}</span>}
                        </Link>
                        <Link to="/profile?tab=appointments" className="header-account-item" role="menuitem" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaCalendarAlt className="header-account-item-icon" aria-hidden="true" />
                          <span>{t('header.myAppointments', 'My appointments')}</span>
                        </Link>
                      </div>

                      <div className="header-account-lang">
                        {renderLanguageChips()}
                      </div>

                      <button onClick={handleLogout} className="header-account-logout" role="menuitem">
                        <FaSignOutAlt className="header-account-item-icon" aria-hidden="true" />
                        <span>{t('common.buttons.logout', 'Log out')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="header-lang-dropdown">
                  <button
                    ref={langToggleRef}
                    type="button"
                    className={`header-lang-toggle${isLanguageDropdownOpen ? ' open' : ''}`}
                    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                    onKeyDown={(e) => handleDropdownKeyDown(e, isLanguageDropdownOpen, setIsLanguageDropdownOpen)}
                    aria-haspopup="true"
                    aria-expanded={isLanguageDropdownOpen}
                    aria-label={t('a11y.selectLanguage', 'Select Language')}
                  >
                    <FaGlobe className="header-lang-icon" aria-hidden="true" />
                    <span className="header-lang-code">{currentLanguage.code.toUpperCase()}</span>
                    <FaChevronDown className={`header-caret${isLanguageDropdownOpen ? ' open' : ''}`} aria-hidden="true" />
                  </button>

                  {isLanguageDropdownOpen && (
                    <div
                      ref={langMenuRef}
                      className="header-lang-menu"
                      role="menu"
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setIsLanguageDropdownOpen(false); langToggleRef.current?.focus(); } }}
                    >
                      {languages.map(lang => (
                        <button
                          key={lang.code}
                          type="button"
                          className={`header-lang-option${lang.code === i18n.language ? ' active' : ''}`}
                          onClick={() => handleLanguageChange(lang.code)}
                          role="menuitem"
                        >
                          <span>{lang.nativeName}</span>
                          {lang.code === i18n.language && <span className="header-lang-check" aria-hidden="true">✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Link to="/login" className="header-login-link" onClick={saveRedirect}>
                  {t('common.buttons.login', 'Log in')}
                </Link>
                <Link to="/register" className="header-cta-btn" onClick={saveRedirect}>
                  <FaUserPlus className="header-btn-icon" aria-hidden="true" />
                  <span>{t('header.createAccount', 'Create free account')}</span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══ Mobil ═══ */}
      {isMobile && (
        <>
          <div className="mobile-header-row">
            <Link to="/" className="header-logo" aria-label="BabynamenIman — Home">
              <img src="/BNI.png" alt="BabynamenIman" className="header-logo-image" fetchpriority="high" loading="eager" />
            </Link>

            <div className="mobile-header-actions">
              {isAuthenticated ? (
                <>
                  <Link to={isUnlimited ? '/profile' : '/credits'} className="header-unlimited-btn mobile">
                    <FaInfinity className="header-unlimited-icon" aria-hidden="true" />
                    <span>{isUnlimited ? t('header.premiumActive', 'Premium Aktif') : t('header.getUnlimited', 'Sınırsız Al')}</span>
                  </Link>
                  <button
                    ref={mobileAccountToggleRef}
                    type="button"
                    className="mobile-avatar-btn"
                    onClick={() => setIsMobileAccountOpen(true)}
                    aria-label={t('a11y.profileMenu', 'Profile Menu')}
                    aria-haspopup="dialog"
                  >
                    <span className="header-avatar" aria-hidden="true">{userInitial}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    ref={mobileLangToggleRef}
                    type="button"
                    className="header-lang-toggle mobile-lang-btn"
                    onClick={() => setIsMobileLangOpen(true)}
                    aria-haspopup="dialog"
                    aria-label={t('a11y.selectLanguage', 'Select Language')}
                  >
                    <FaGlobe className="header-lang-icon" aria-hidden="true" />
                    <span className="header-lang-code">{currentLanguage.code.toUpperCase()}</span>
                  </button>
                  <Link to="/login" className="header-login-link mobile" onClick={saveRedirect}>
                    {t('common.buttons.login', 'Log in')}
                  </Link>
                  <Link to="/register" className="header-cta-btn mobile" onClick={saveRedirect}>
                    {t('common.buttons.register', 'Sign up')}
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="mobile-header-bottom">
            <form className="header-search mobile-search" role="search" onSubmit={handleSearchSubmit}>
              <svg className="header-search-glyph" aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
                <line x1="15.6" y1="15.6" x2="21" y2="21" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              <input
                ref={searchInputRef}
                type="search"
                className="header-search-input"
                placeholder={t('header.searchPlaceholder', "Look up a name's meaning…")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                aria-label={t('header.searchPlaceholder', "Look up a name's meaning…")}
              />
            </form>
          </div>
        </>
      )}

      </header>

    {/* ═══ Mobil — Hesap bottom sheet ═══
        NOT: header'in backdrop-filter'i fixed konumlandirma icin containing
        block olusturur; sheet'ler header icinde kalirsa backdrop viewport
        yerine header'a hapsolur. Bu yuzden sheet'ler header DISINDA. */}
    {isMobile && isMobileAccountOpen && (
        <div className="mobile-sheet-backdrop" onClick={() => setIsMobileAccountOpen(false)}>
          <div
            className="mobile-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t('a11y.profileMenu', 'Profile Menu')}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mobile-sheet-grip" aria-hidden="true"></span>

            <div className="mobile-sheet-user">
              <span className="header-avatar header-avatar--lg" aria-hidden="true">{userInitial}</span>
              <div className="header-account-head-text">
                <span className="header-account-fullname">{user?.name || userName}</span>
                <span className="header-account-plan">{planLine}</span>
              </div>
            </div>

            <div className="mobile-sheet-grid">
              <Link to="/profile" className="mobile-sheet-tile" onClick={() => setIsMobileAccountOpen(false)}>
                <FaUser className="mobile-sheet-tile-icon" aria-hidden="true" />
                <span>{t('common.navigation.profile', 'Profile')}</span>
              </Link>
              <Link to="/favorites" className="mobile-sheet-tile" onClick={() => setIsMobileAccountOpen(false)}>
                <FaHeart className="mobile-sheet-tile-icon" aria-hidden="true" />
                <span>{t('common.navigation.favorites', 'Favorites')}{favCount > 0 ? ` · ${favCount}` : ''}</span>
              </Link>
              <Link to="/my-ebooks" className="mobile-sheet-tile" onClick={() => setIsMobileAccountOpen(false)}>
                <FaBook className="mobile-sheet-tile-icon" aria-hidden="true" />
                <span>{t('common.navigation.myEbooks', 'My E-Books')}{ebookCount > 0 ? ` · ${ebookCount}` : ''}</span>
              </Link>
              <Link to="/profile?tab=appointments" className="mobile-sheet-tile" onClick={() => setIsMobileAccountOpen(false)}>
                <FaCalendarAlt className="mobile-sheet-tile-icon" aria-hidden="true" />
                <span>{t('header.myAppointments', 'My appointments')}</span>
              </Link>
            </div>

            {renderLanguageChips()}

            <button type="button" className="mobile-sheet-logout" onClick={handleLogout}>
              <FaSignOutAlt aria-hidden="true" />
              <span>{t('common.buttons.logout', 'Log out')}</span>
            </button>
          </div>
        </div>
      )}

      {/* ═══ Mobil — Dil bottom sheet ═══ */}
      {isMobile && isMobileLangOpen && (
        <div className="mobile-sheet-backdrop" onClick={() => setIsMobileLangOpen(false)}>
          <div
            className="mobile-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t('a11y.selectLanguage', 'Select Language')}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mobile-sheet-grip" aria-hidden="true"></span>
            <span className="mobile-sheet-title">{t('common.language', 'Language')}</span>
            {languages.map(lang => (
              <button
                key={lang.code}
                type="button"
                className={`mobile-sheet-lang-option${lang.code === i18n.language ? ' active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span>{lang.nativeName}</span>
                {lang.code === i18n.language && <span className="header-lang-check" aria-hidden="true">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
